import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { coerceUrl } from '../utils/url';

// Lead-magnet contract: the marketing site redirects to /signup with these
// query params so the user's first trackable link can be created automatically.
//   plan            'free' | 'pro'
//   smLink          social media URL (encoded)
//   destinationUrl  destination URL (encoded; should host or link to a Typeform)
//   offer           optional short string describing the offer
// All four are forwarded through to /dashboard so ModernDashboard can run the
// validate-and-auto-create flow before showing onboarding.

const Signup: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { register } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Get plan from URL query parameter (free or pro)
  const planTier = searchParams.get('plan') || 'free';

  // Lead-magnet carry-through. Empty string when absent; coerceUrl returns
  // null for malformed input — we silently drop bad values rather than
  // surfacing an error here (the user didn't type these). The dashboard
  // does the meaningful validation.
  const carriedSmLink = coerceUrl(searchParams.get('smLink') || '') || '';
  const carriedDestinationUrl = coerceUrl(searchParams.get('destinationUrl') || '') || '';
  const carriedOffer = (searchParams.get('offer') || '').trim();
  const hasCarriedLeadMagnet = !!(carriedSmLink && carriedDestinationUrl);

  const buildDashboardUrl = () => {
    const qs = new URLSearchParams();
    if (carriedSmLink) qs.set('smLink', carriedSmLink);
    if (carriedDestinationUrl) qs.set('destinationUrl', carriedDestinationUrl);
    if (carriedOffer) qs.set('offer', carriedOffer);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return `/dashboard${suffix}`;
  };

  useEffect(() => {
    // Validate plan tier
    if (planTier !== 'free' && planTier !== 'pro') {
      setErrorMessage('Invalid plan selected. Defaulting to free plan.');
    }
  }, [planTier]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    // Validation
    if (password !== confirmPassword) {
      setErrorMessage('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setErrorMessage('Password must be at least 8 characters long');
      return;
    }

    setIsSubmitting(true);

    try {
      // Register the user (orgId is auto-generated by backend)
      await register(email, password, firstName, lastName);

      // If paid plan, redirect to Stripe checkout
      if (planTier === 'pro') {
        // Stripe round-trip strips our query params, so stash the lead-magnet
        // values in sessionStorage. ModernDashboard reads sessionStorage on
        // mount before falling back to URL params.
        if (hasCarriedLeadMagnet) {
          try {
            sessionStorage.setItem('pendingLeadMagnet', JSON.stringify({
              smLink: carriedSmLink,
              destinationUrl: carriedDestinationUrl,
              offer: carriedOffer
            }));
          } catch (_) { /* storage disabled; the link just won't auto-create */ }
        }

        const response = await api.subscription.createCheckoutSession(planTier);

        if (response.success && response.url) {
          window.location.href = response.url;
        } else {
          throw new Error('Failed to create checkout session');
        }
      } else {
        // For free plan, forward the lead-magnet params via URL.
        navigate(buildDashboardUrl());
      }
    } catch (error: any) {
      console.error('Signup error:', error);
      setErrorMessage(error.message || 'Registration failed. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{ maxWidth: '500px', width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <img
            src="/revenue-social-logo.svg"
            alt="Revenue Social"
            style={{
              height: '80px',
              width: 'auto',
              marginBottom: '16px'
            }}
          />
          <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#1e293b', marginBottom: '8px' }}>
            Create Your Account
          </h1>
          <p style={{ color: '#666', fontSize: '16px' }}>
            Sign up for {planTier === 'pro' ? 'Pro' : 'Free'} plan
          </p>
        </div>

        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
          padding: '32px'
        }}>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                  First Name
                </label>
                <input
                  type="text"
                  placeholder="First name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '16px',
                    outline: 'none'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                  Last Name
                </label>
                <input
                  type="text"
                  placeholder="Last name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '16px',
                    outline: 'none'
                  }}
                />
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                Email Address
              </label>
              <input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '16px',
                  outline: 'none'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                Password
              </label>
              <input
                type="password"
                placeholder="Create password (min 8 characters)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '16px',
                  outline: 'none'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                Confirm Password
              </label>
              <input
                type="password"
                placeholder="Confirm your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '16px',
                  outline: 'none'
                }}
              />
            </div>

            {errorMessage && (
              <div style={{
                marginBottom: '16px',
                padding: '12px',
                backgroundColor: '#fef2f2',
                borderRadius: '8px',
                border: '1px solid #fecaca',
                color: '#dc2626',
                fontSize: '14px'
              }}>
                {errorMessage}
              </div>
            )}

            {planTier === 'pro' && (
              <div style={{
                marginBottom: '16px',
                padding: '12px',
                backgroundColor: '#eff6ff',
                borderRadius: '8px',
                border: '1px solid #bfdbfe',
                color: '#1e40af',
                fontSize: '14px'
              }}>
                After registration, you'll be redirected to complete payment for the Pro plan ($89.99/month).
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                width: '100%',
                backgroundColor: isSubmitting ? '#9ca3af' : '#2563eb',
                color: 'white',
                fontWeight: '600',
                padding: '12px 16px',
                borderRadius: '8px',
                border: 'none',
                fontSize: '16px',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                opacity: isSubmitting ? 0.7 : 1
              }}
            >
              {isSubmitting ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>

          <div style={{
            marginTop: '24px',
            textAlign: 'center',
            fontSize: '14px',
            color: '#6b7280'
          }}>
            Already have an account?{' '}
            <a
              href="/"
              style={{
                color: '#2563eb',
                textDecoration: 'none',
                fontWeight: '600'
              }}
            >
              Sign In
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
